import React, { useRef, useState, useEffect } from 'react'
import { IEmbedSubmission, IProcessObject } from 'types'
import Spreadsheet from 'components/spreadsheet'
import { PERMISSIONS } from 'helpers/auth'
import { findSection } from 'components/document/helpers/functions'
import { useOptionalDataContext } from 'components/document/contexts/data/useOptionalDataContext'
import { useAuth } from 'hooks/auth'
import useOnScreen from 'hooks/useOnScreen'
import EmbedResizeHandle from './EmbedResizeHandle'
import EmbedEditButton from './EmbedEditButton'
import EmbedSubmissionBar from './EmbedSubmissionBar'
import useEmbedControls from '../hooks/useEmbedControls'

export interface DashboardProps {
  blockProps: {
    views: { tableId: string; viewId: string; showToolbar?: boolean; showAggregate?: boolean; submissionId?: string }[]
    columns: number
    height: number
    process?: IProcessObject
    sectionId?: string
    entityKey?: string
    openDashboardDialog?: (anchor: HTMLElement, entityKey?: string) => void
    onResizeHeight?: (entityKey: string, height: number) => void
  }
}

// The section's submission a view holds. Ids match whatever their case, as on the server, which
// stores them lower-case while a view may hold one written otherwise (or, written through the API, not
// as a string at all).
export const findEmbedSubmission = (
  submissions: IEmbedSubmission[] | undefined,
  submissionId: unknown
): IEmbedSubmission | undefined =>
  submissionId
    ? submissions?.find((candidate) => candidate.publicId.toLowerCase() === String(submissionId).toLowerCase())
    : undefined

const Dashboard: React.FC<DashboardProps> = ({ blockProps }) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const isVisible = useOnScreen(wrapperRef)
  const [visible, setVisible] = useState<boolean>(false)
  // The saved height is a row height.
  const controls = useEmbedControls({
    savedHeight: blockProps.height,
    entityKey: blockProps.entityKey,
    editable: !!blockProps.openDashboardDialog,
    onResizeHeight: blockProps.onResizeHeight
  })

  useEffect(() => {
    if (isVisible && !visible) {
      setVisible(true)
    }
  }, [isVisible])

  // Force-render when the document export needs to capture chart canvases.
  useEffect(() => {
    const handleForceRender = () => setVisible(true)
    window.addEventListener('force-render-embeds', handleForceRender)
    return () => window.removeEventListener('force-render-embeds', handleForceRender)
  }, [])

  // Each view may carry its own submission, kept on the Document's section.
  const dataContext = useOptionalDataContext()

  let permissionCap = PERMISSIONS.contributor
  if (blockProps.process) {
    if (blockProps.process.permissionLevel <= PERMISSIONS.contributor) {
      permissionCap = blockProps.process.permissionLevel
    }
  }
  // The Document's own cap applies too: a template preview shows its tables read-only.
  if (dataContext?.permissionCap !== undefined && dataContext.permissionCap < permissionCap) {
    permissionCap = dataContext.permissionCap
  }
  const { user } = useAuth()
  const contextProcess = dataContext?.process
  const section =
    blockProps.sectionId && contextProcess ? findSection(blockProps.sectionId, contextProcess).section : null
  const submissionFor = (submissionId: string | undefined): IEmbedSubmission | undefined =>
    findEmbedSubmission(section?.submissions, submissionId)

  // Matches the server: contributors and above submit; the owner, or whoever submitted whatever their role
  // now, resets. Submissions live on the Document's sections, so these only matter inside a Document, whose
  // provider's flags apply its permission cap: a Document capped below contributor (a template preview)
  // offers neither.
  const isOwner = !!dataContext?.isAdmin
  const canSubmit = !!dataContext && (dataContext.isAdmin || dataContext.isContributor)
  const cappedBelowContributor =
    dataContext?.permissionCap !== undefined && dataContext.permissionCap < PERMISSIONS.contributor
  const canResetSubmission = (submission: IEmbedSubmission): boolean =>
    isOwner || (!cappedBelowContributor && !!user && submission.submittedBy?.publicId === user.publicId)

  const updateSubmissions = (submissions: IEmbedSubmission[]): void => {
    if (blockProps.sectionId && dataContext) dataContext.patchSection(blockProps.sectionId, { submissions }, true)
  }

  // The saved height is per row, so dragging the bottom edge shares the change across every row.
  const rowCount = Math.max(1, Math.ceil(blockProps.views.length / Math.max(1, blockProps.columns)))

  return (
    <div ref={wrapperRef} className="dashboard-wrapper w-full">
      {visible ? (
        <div
          {...controls.viewProps}
          data-testid="embedded-dashboard-view"
          className="relative grid w-full gap-2.5 mb-[5px]"
          style={{ gridTemplateColumns: `repeat(${blockProps.columns}, 1fr)` }}
        >
          {blockProps.views.map((view, idx) => {
            const submission = submissionFor(view.submissionId)
            // A submitted view is read-only until it's reset.
            const viewCap = submission?.submittedAt ? PERMISSIONS.viewer : permissionCap
            return (
              <div
                key={`${view.tableId}-${view.viewId}-${idx}`}
                data-testid="dashboard-view-panel"
                className="flex flex-col overflow-x-auto"
                style={{ height: `${controls.height}px` }}
              >
                <div className="flex-1 min-h-0">
                  <Spreadsheet
                    // Remount when the cap changes so a newly locked (or reset) view picks up its new access level.
                    key={`${view.tableId}-${view.viewId}-${viewCap}`}
                    tableId={view.tableId}
                    tableViewId={view.viewId}
                    processId={blockProps.process ? blockProps.process.publicId : undefined}
                    processSectionId={blockProps.sectionId ? blockProps.sectionId : undefined}
                    permissionCap={viewCap}
                    process={blockProps.process ? blockProps.process : undefined}
                    showToolbar={view.showToolbar}
                    showAggregate={view.showAggregate}
                    fitToHeight
                  />
                </div>
                {submission && blockProps.process && blockProps.sectionId && (
                  <EmbedSubmissionBar
                    submission={submission}
                    processId={blockProps.process.publicId}
                    sectionId={blockProps.sectionId}
                    canReset={canResetSubmission(submission)}
                    canSubmit={canSubmit}
                    onChange={updateSubmissions}
                  />
                )}
              </div>
            )
          })}
          {controls.showEditButton && (
            <EmbedEditButton
              label="Edit Dashboard"
              title="Edit dashboard"
              onClick={(anchor) => blockProps.openDashboardDialog!(anchor, blockProps.entityKey)}
            />
          )}
          {controls.canResize && (
            <EmbedResizeHandle
              {...controls.resizeHandleProps}
              dragScale={1 / rowCount}
              heightSuffix={rowCount > 1 ? ' per row' : ''}
            />
          )}
        </div>
      ) : (
        <div className="w-full" style={{ height: `${controls.height}px` }} />
      )}
    </div>
  )
}

export default Dashboard
